import type { OptionsDocument, QuoteDocument, QuoteTotals } from 'paintscout'
// Deep imports, not the builder barrel: this module runs inside the calculator, and the builder
// barrel imports back into the calculator.
import { hasTierProductSwaps } from '../../builder/quote/tiered-packages/apply-tier-products'
import {
  getTieredPackageBasePrice,
  resolveTieredPackageOptionsAdjustment,
  resolveTieredPackageTierPrice
} from '../../builder/quote/tiered-packages/resolve-tier-prices'
import { parseNumber } from '../util/parse-number'
import { roundPrice } from '../util'

/**
 * Tiered Packages (Good / Better / Best).
 *
 * Computes the price delta of the currently-selected tier relative to the estimate as built and
 * stores it on `quote.totals.tier_adjustment`. The base tier is the estimate as built, so when the
 * selected tier IS the base tier (or the feature is off) the delta is 0.
 *
 * The delta is rendered as its own row on the total table and is folded into the taxable total
 * exactly once, inside `discount()` / `discounts()` (same treatment as addedOptions). Tier prices
 * come from `resolveTieredPackageTierPrice`, the same resolver `getTierPrices` uses, so the card
 * price and the applied total always agree.
 */
export function tierAdjustment(quote: QuoteDocument, _options: OptionsDocument): QuoteTotals {
  const newTotal = { ...quote.totals }

  const tieredPackages = quote.tieredPackages
  const selectedTierId = tieredPackages?.selectedTierId

  if (
    !tieredPackages ||
    !tieredPackages.enabled ||
    !tieredPackages.tiers ||
    selectedTierId === tieredPackages.baseTierId
  ) {
    // No adjustment applies — clear any stale value rather than writing a 0, so quotes
    // not using the feature keep their original totals shape (and switching back to the
    // base tier removes the field).
    delete newTotal.tier_adjustment
    return newTotal
  }

  // A tier the estimator has not priced by hand, whose products were swapped, is already paid for:
  // the substrates on the quote changed, so its price sits in `totals.price`. Adding its adjustment
  // on top would charge for the upgrade twice. An overridden tier is a typed target price, so it
  // holds even against the swap.
  if (
    !tieredPackages.tiers[selectedTierId]?.overridden &&
    hasTierProductSwaps({ tieredPackages, tierId: selectedTierId })
  ) {
    delete newTotal.tier_adjustment
    return newTotal
  }

  const subTotal = parseNumber(quote.totals.price)
  const targetPrice = resolveTieredPackageTierPrice({
    tieredPackages,
    tierId: selectedTierId,
    basePrice: getTieredPackageBasePrice({ quote })
  })

  // Optional items hold their price unless this tier opted in — see the resolver.
  const optionsTotal = parseNumber(quote.totals.addedOptions?.price) + parseNumber(quote.totals.addedOptions?.materials)
  const optionsAdjustment = resolveTieredPackageOptionsAdjustment({
    tieredPackages,
    tierId: selectedTierId,
    optionsTotal
  })

  const adjustment = roundPrice(targetPrice - subTotal + optionsAdjustment)

  if (!adjustment) {
    delete newTotal.tier_adjustment
    return newTotal
  }

  newTotal.tier_adjustment = adjustment

  return newTotal
}
